import { defineStore } from 'pinia';
import { i18n } from 'src/boot/i18n';
import type { dynamicObj } from 'src/pages/assets';
import { supplierDocTypeKey, CONSTANTS, DEFAULT_LANGUAGE } from 'src/constants';
import { LocalStorage, SessionStorage } from 'quasar';
import displayFieldLabel from 'src/utils/displayFieldLabel';
import { setWithExpiry, getWithExpiry } from 'src/utils/cacheHelper';
import { useUserStore, useMasterDataLookupStore } from 'src/store/main';
import {
  documentTypeApiService,
  userApiService,
  docStatusNotificationsApiService,
  subOrgsApiService,
  operatorApiService,
  documentPOApiService,
} from 'src/services';
import {
  aiModelsAndLabelsApiService,
  decisionApiService,
  exportApiService,
  masterDataApiService,
  workflowApiService,
  emailTemplateService,
} from 'src/services';

export const useDropdownStore = defineStore('dropdown', {
  state: () => ({
    usersMap: new Map(),
    time_list: [] as any,
    usersList: [] as any,
    user_list_loading: false,
    subOrgList: [] as any,
    subOrgMap: new Map<string, string>(),
    sub_org_list_loading: false,
    supplierNameMap: new Map<string, string>(),
    supplier_name_map_loading: false,
    booleanList: [] as any,
    trigger_list: [] as any,
    doc_type_list: [] as any,
    document_statuses_list: [] as any,
    doc_type_loading: false,
    doc_status_list: [] as any,
    priority_list: [] as any,
    active_doc_type_list: [] as any,
    doc_types_sub_types_list: [] as any,
    active_doc_types_sub_types_list: [] as any,
    classification_type_list: [] as any,
    sub_types_list: [] as any,
    po_module_list: [] as any,
    model_list: [] as any,
    encryptions: ['SSL', 'TLS'],
    protocols: ['IMAP', 'OAuth Office365', 'OAuth Office365 - Tenant'],
    ftp_types: ['FTP', 'FTPS', 'SFTP'],
    languages_list: [] as any,
    tableMap: new Map(),
    table_name_list_loading: false,
    table_name_list: [] as any,
    table_columnMap: new Map(),
    table_column_name_list_loading: false,
    table_column_name_list: [] as any,
    groupsMap: new Map(),
    groups_list: [] as any,
    groups_list_loading: false,
    workflow_list: [] as any,
    workflow_list_loading: false,
    master_data_tableMap: new Map(),
    master_data_table_list_loading: false,
    master_data_table_list: [] as any,
    export_configurationMap: new Map(),
    export_configuration_list_loading: false,
    export_configuration_list: [] as any,
    decision_tableMap: new Map(),
    decision_table_list_loading: false,
    decision_table_list: [] as any,
    doc_operatorMap: new Map(),
    doc_operator_prompts_loading: false,
    doc_operator_prompts: [] as any,
    po_display_colsMap: new Map(),
    po_display_cols_loading: false,
    po_display_cols: [] as any,
    email_template_listMap: new Map(),
    email_template_list_loading: false,
    email_template_list: [] as any,
    wf_db_values: {} as any,

    custom_date_range: {} as any,
    accounting_export_types: [
      { value: 'ln', label: 'LN' },
      { value: 'm3', label: 'M3' },
    ],
    script_trigger_list: [
      'AFTER_EXTRACTION',
      'BEFORE_TRANSFORMATION',
      'AFTER_TRANSFORMATION',
      'BEFORE_VALIDATION',
      'AFTER_VALIDATION',
      'BEFORE_FORMATTING',
      'AFTER_FORMATTING',
      'ON_FIELD_CHANGE',
      'ON_TABLE_CHANGE',
      'BEFORE_PO_MATCHING',
      'ON_SAVE',
      'APPROVAL',
      'ALL',
    ],
    microservice_list: [] as any,
  }),
  getters: {
    // Reactive language list that re-translates labels whenever i18n locale changes
    // All 22 Tolgee translations with flags from flagcdn.com
    languagesListReactive: (state) => {
      return [
        { value: DEFAULT_LANGUAGE, label: i18n.t('english'), flag: 'us' },
        { value: 'de', label: i18n.t('german'), flag: 'de' },
        { value: 'es-es', label: i18n.t('spanish'), flag: 'es' },
        { value: 'fr', label: i18n.t('french'), flag: 'fr' },
        { value: 'nl', label: i18n.t('dutch'), flag: 'nl' },
        { value: 'it', label: i18n.t('italian'), flag: 'it' },
        { value: 'zh', label: i18n.t('chinese'), flag: 'cn' },
        { value: 'bg', label: i18n.t('bulgarian'), flag: 'bg' },
        { value: 'cs', label: i18n.t('czech'), flag: 'cz' },
        { value: 'hu', label: i18n.t('hungarian'), flag: 'hu' },
        { value: 'ja', label: i18n.t('japanese'), flag: 'jp' },
        { value: 'ko', label: i18n.t('korean'), flag: 'kr' },
        { value: 'pl', label: i18n.t('polish'), flag: 'pl' },
        { value: 'pt', label: i18n.t('portuguese'), flag: 'pt' },
        { value: 'pt-br', label: i18n.t('portuguese_br'), flag: 'br' },
        { value: 'ro', label: i18n.t('romanian'), flag: 'ro' },
        { value: 'sk', label: i18n.t('slovak'), flag: 'sk' },
        { value: 'sl', label: i18n.t('slovenian'), flag: 'si' },
        { value: 'sv', label: i18n.t('swedish'), flag: 'se' },
        { value: 'th', label: i18n.t('thai'), flag: 'th' },
        { value: 'tr', label: i18n.t('turkish'), flag: 'tr' },
        { value: 'vi', label: i18n.t('vietnamese'), flag: 'vn' },
      ];
    },
  },
  actions: {
    async prepareDecisionTableDropdown() {
      try {
        this.decision_table_list_loading = true;
        // if list is already filled do not call api agian and again
        if (this.decision_table_list.length > 1) {
          this.decision_table_list_loading = false;
          return;
        }
        this.decision_table_list = [];
        this.decision_tableMap.clear();
        const response = await decisionApiService.getAllDecisionTrees(true);
        if (!response.hasOwnProperty('success') || response.success === false) {
          throw response;
        }
        for (const decision_tableObj of response.data) {
          this.decision_tableMap.set(decision_tableObj.id, decision_tableObj);
          this.decision_table_list.push({
            value: decision_tableObj.id,
            valueStr: decision_tableObj.id.toString(),
            label: decision_tableObj.name,
          });
        }
      } catch (err) {
        logError(err);
      } finally {
        this.decision_table_list_loading = false;
      }
    },

    async prepareDocOperatorDropdown() {
      try {
        this.doc_operator_prompts_loading = true;
        // if list is already filled do not call api agian and again
        if (this.doc_operator_prompts.length > 1) {
          this.doc_operator_prompts_loading = false;
          return;
        }
        this.doc_operator_prompts = [];
        this.doc_operatorMap.clear();
        const response = await operatorApiService.getAllPrompts(false);
        if (!response.hasOwnProperty('success') || response.success === false) {
          throw response;
        }
        for (const scriptObj of response.data) {
          this.doc_operatorMap.set(scriptObj.id, scriptObj);
          if (scriptObj.prompt_variables) {
            const scriptObjPromptVariable = JSON.parse(
              scriptObj.prompt_variables,
            );
            for (const key of Object.keys(scriptObjPromptVariable)) {
              scriptObjPromptVariable[key] = '';
            }
            scriptObj.prompt_variables = JSON.stringify(
              scriptObjPromptVariable,
            );
          }
          this.doc_operator_prompts.push({
            value: scriptObj.id,
            valueStr: scriptObj.id.toString(),
            label: scriptObj.prompt_name,
            variables: scriptObj.prompt_variables,
            sensitive_variables: JSON.parse(scriptObj.sensitive_variables),
          });
        }
      } catch (err) {
        logError(err);
      } finally {
        this.doc_operator_prompts_loading = false;
      }
    },

    async preparePODisplayColsDropdown(in_po_items = true) {
      try {
        this.po_display_cols_loading = true;
        if (this.po_display_cols.length > 1) {
          this.po_display_cols_loading = false;
          return;
        }

        const master_data_lookup = useMasterDataLookupStore();
        const mappings =
          await master_data_lookup.getColumnLabelPreference('PURCHASE_ORDER');

        const poCustomColumnMappings = mappings.reduce(
          (acc: any, item: any) => {
            acc[item.field] = item.label;

            return acc;
          },
          {},
        );

        this.po_display_cols = [];
        this.po_display_colsMap.clear();

        const response = await documentPOApiService.get_all_display_cols();
        if (!response.hasOwnProperty('success') || response.success === false) {
          throw response;
        }
        for (const scriptObj of response.data) {
          this.po_display_colsMap.set(scriptObj.id, scriptObj);
          if (scriptObj.in_po_items != in_po_items) {
            continue;
          }

          this.po_display_cols.push({
            value: scriptObj.name,
            valueStr: scriptObj.name.toString(),
            label: poCustomColumnMappings[scriptObj.name]
              ? poCustomColumnMappings[scriptObj.name]
              : i18n.t(scriptObj.translation_key),
          });
        }
      } catch (err) {
        logError(err);
      } finally {
        this.po_display_cols_loading = false;
      }
    },

    async resetDocTypesSubDocTypes() {
      LocalStorage.remove('doc_types');
      SessionStorage.remove('doc_types_sub_types');
      SessionStorage.remove('sub_types');
      await this.getDocTypesSubTypes();
    },

    fillDocTypeAndSubTypesListIfEmpty() {
      const docTypeList = getWithExpiry('doc_types_sub_types');
      const subTypesList = getWithExpiry('sub_types');

      this.getDocTypesSubTypes();
      return;
      if (docTypeList && subTypesList) {
        this.doc_types_sub_types_list = docTypeList;
        this.sub_types_list = subTypesList;
      } else {
        this.getDocTypesSubTypes();
      }
    },
    async prepareDocTypeAndSubTypesList() {
      if (this.doc_type_list.length > 0) {
        return;
      }
      await this.getDocTypesSubTypes();
    },

    resetDocTypeLists() {
      this.doc_types_sub_types_list = [];
      this.sub_types_list = [];
      this.doc_type_list = [];
      this.active_doc_type_list = [];
      this.active_doc_types_sub_types_list = [];
    },
    async getDocTypesSubTypes() {
      if (this.doc_type_loading) {
        return;
      }
      this.doc_type_loading = true;
      this.resetDocTypeLists();

      let hasSubDocTypes = false;
      const response = await documentTypeApiService.fetchDocTypesSubTypes();
      const docs = Array.isArray(response?.data) ? response.data : [];
      if (!docs.length) {
        this.doc_type_loading = false;
        return;
      }
      const filterData = docs.filter(
        (doc: any) =>
          !supplierDocTypeKey.includes(doc.document_key) &&
          !(doc.is_active === false),
      );
      filterData.forEach((docObj: any, index: any) => {
        const docTypeLabel =
          displayFieldLabel.methods.displayFieldLabel(docObj);
        hasSubDocTypes =
          docObj.sub_document_types &&
          Array.isArray(docObj.sub_document_types) &&
          docObj.sub_document_types.length > 0;

        const parent_type = Object.assign(
          {},
          {
            value: docObj.document_key,
            name: docObj.document_name,
            label: docTypeLabel,
            hasDocTypes: hasSubDocTypes,
            is_parent: true,
          },
        );
        // add to lists
        this.doc_type_list.push(parent_type);
        this.doc_types_sub_types_list.push(parent_type);

        // for ACTIVE
        if (docObj.is_active == true) {
          this.active_doc_types_sub_types_list.push(parent_type);
          this.active_doc_type_list.push(parent_type);
        }

        if (hasSubDocTypes) {
          const sub_document_types = docObj.sub_document_types;

          for (const sub_doc_type of sub_document_types) {
            const subDocTypeLabel =
              displayFieldLabel.methods.displayFieldLabel(sub_doc_type);

            const sub_type = Object.assign(
              {},
              {
                value: sub_doc_type.sub_doc_type_key,
                label: subDocTypeLabel,
                doc_type_key: docObj.document_key,
                name: sub_doc_type.document_name,
                hasDocTypes: false,
                is_parent: false,
              },
            );
            this.doc_types_sub_types_list.push(sub_type);
            this.active_doc_types_sub_types_list.push(sub_type);
            this.sub_types_list.push(
              Object.assign(sub_type, {
                model_label_id: sub_doc_type.model_label_id,
              }),
            );
          }
        }
      });
      this.doc_type_loading = false;
      setWithExpiry('doc_types_sub_types', this.doc_types_sub_types_list);
      setWithExpiry('sub_types', this.sub_types_list);
    },

    async prepareEmailTemplateDropdown() {
      try {
        this.email_template_list_loading = true;
        // if list is already filled do not call api agian and again
        if (this.email_template_list.length > 1) {
          this.email_template_list_loading = false;
          return;
        }
        this.email_template_list = [];
        this.email_template_listMap.clear();
        const response =
          await emailTemplateService.getEmailTemplateListByDocType(null);
        if (!response.hasOwnProperty('success') || response.success === false) {
          throw response;
        }
        for (const email_templateObj of response.data) {
          this.email_template_listMap.set(
            email_templateObj.id,
            email_templateObj,
          );
          this.email_template_list.push({
            value: email_templateObj.id,
            valueStr: email_templateObj.id.toString(),
            label: email_templateObj.name,
            doc_type: email_templateObj.doc_type,
          });
        }
      } catch (err) {
        logError(err);
      } finally {
        this.email_template_list_loading = false;
      }
    },

    async prepareExportConfigurationDropdown() {
      this.export_configuration_list_loading = true;
      // if list is already filled do not call api agian and again
      if (this.export_configuration_list.length > 1) {
        this.export_configuration_list_loading = false;
        return;
      }
      this.export_configuration_list = [];
      this.export_configurationMap.clear();
      const response = await exportApiService.getAll();
      if (!response) {
        this.export_configuration_list_loading = false;
        return;
      }

      for (const export_configurationObj of response.data) {
        this.export_configurationMap.set(
          export_configurationObj.id,
          export_configurationObj,
        );
        this.export_configuration_list.push({
          value: export_configurationObj.id,
          valueStr: export_configurationObj.id.toString(),
          label: export_configurationObj.title,
        });
      }
      this.export_configuration_list_loading = false;
    },

    async prepareUsersDropdown() {
      this.user_list_loading = true;

      this.usersList = [];
      this.usersMap.clear();

      const userStore = useUserStore();
      await userStore.getAllUsers();

      if (!Array.isArray(userStore.allUsers)) {
        this.user_list_loading = false;
        return;
      }

      for (const userObj of userStore.allUsers) {
        if (userObj?.id === null || userObj?.id === undefined) {
          continue;
        }

        const firstName = userObj.first_name ?? '';
        const lastName = userObj.last_name ?? '';
        const label =
          `${firstName} ${lastName}`.trim() ||
          userObj.email ||
          userObj.username ||
          String(userObj.id);

        this.usersMap.set(userObj.id, userObj);
        this.usersList.push({
          value: userObj.id,
          valueStr: String(userObj.id),
          label,
        });
      }
      this.user_list_loading = false;
    },

    async prepareGroupsDropdown() {
      this.groups_list_loading = true;
      // if list is already filled do not call api agian and again
      if (this.groups_list.length > 1) {
        this.groups_list_loading = false;
        return;
      }
      this.groups_list = [];
      this.groupsMap.clear();
      const response = await userApiService.getGroups();
      if (!response) {
        this.groups_list_loading = false;
        return;
      }

      for (const groupObj of response) {
        this.groupsMap.set(groupObj.id, groupObj);
        this.groups_list.push({
          value: groupObj.id,
          valueStr: groupObj.id.toString(),
          label: groupObj.name,
        });
      }
      this.groups_list_loading = false;
    },

    async prepareWorkflowNameDropdown() {
      this.workflow_list_loading = true;
      if (this.workflow_list.length > 1) {
        this.workflow_list_loading = false;
        return;
      }
      this.workflow_list = [];
      const response = await workflowApiService.getWorkflowsForOrg();
      if (!response || !Array.isArray(response)) {
        this.workflow_list_loading = false;
        return;
      }
      for (const workflowObj of response) {
        this.workflow_list.push({
          label: workflowObj.name,
          value: workflowObj.id,
          valueStr: workflowObj.id.toString(),
        });
      }
      this.workflow_list_loading = false;
    },

    async prepareMasterDataTableDropdown() {
      this.master_data_table_list_loading = true;
      if (this.master_data_table_list.length > 1) {
        this.master_data_table_list_loading = false;
        return;
      }
      this.master_data_table_list = [];
      this.master_data_tableMap.clear();
      const response = await masterDataApiService.getTypes();
      if (!response) {
        this.master_data_table_list_loading = false;
        return;
      }

      for (const master_data_tableObj of response) {
        this.master_data_tableMap.set(
          master_data_tableObj,
          master_data_tableObj,
        );
        this.master_data_table_list.push({
          value: master_data_tableObj,
          valueStr: master_data_tableObj.toString(),
          label: master_data_tableObj,
        });
      }
      this.master_data_table_list_loading = false;
    },

    async prepareTableNameDropdown() {
      this.table_name_list_loading = true;
      if (this.table_name_list.length > 1) {
        this.table_name_list_loading = false;
        return;
      }
      this.table_name_list = [];
      this.tableMap.clear();
      const response = await documentTypeApiService.getAllDocumentTables();
      if (!response) {
        this.table_name_list_loading = false;
        return;
      }

      for (const tableObj of response.data) {
        this.tableMap.set(tableObj.table_name, tableObj);
        this.table_name_list.push({
          value: tableObj.table_name,
          valueStr: tableObj.table_name.toString(),
          label: i18n.t(tableObj.table_name),
        });
      }
      this.table_name_list_loading = false;
    },

    async prepareAllDocumentTableColumnsDropdown() {
      this.table_column_name_list_loading = true;
      if (this.table_column_name_list.length > 1) {
        this.table_column_name_list_loading = false;
        return;
      }
      this.table_column_name_list = [];
      this.table_columnMap.clear();

      try {
        const response =
          await documentTypeApiService.getAllDocumentTableColumns();

        if (!response || !response.success) {
          this.table_column_name_list_loading = false;
          return;
        }

        for (const columnObj of response.data) {
          if (this.table_columnMap.has(columnObj.column_name)) {
            for (let i = 0; i < this.table_column_name_list.length; i++) {
              if (
                this.table_column_name_list[i].value == columnObj.column_name
              ) {
                this.table_column_name_list[i].tables.push(
                  columnObj.table_name,
                );
                break;
              }
            }
          } else {
            this.table_columnMap.set(columnObj.column_name, columnObj);
            this.table_column_name_list.push({
              value: columnObj.column_name,
              valueStr: columnObj.column_name.toString(),
              label: i18n.t(columnObj.column_name),
              display_name: columnObj.title,
              table_name: columnObj.table_name,
              tables: [columnObj.table_name],
            });
          }
        }
      } catch (e: any) {
        throw { message: e };
      } finally {
        this.table_column_name_list_loading = false;
      }
    },

    async resetSubOrgsList() {
      this.subOrgList = [];
      await this.getSubOrgsList();
    },

    async getSubOrgsList() {
      this.sub_org_list_loading = true;
      if (this.subOrgList.length > 1) {
        this.sub_org_list_loading = false;
        return;
      }

      this.subOrgList = [];
      this.subOrgMap.clear();
      try {
        const response = await subOrgsApiService.getSubOrgsWithUsers();
        if (!response) {
          this.sub_org_list_loading = false;
          return;
        }

        for (const [index, item] of Object.entries(response)) {
          const { id, name, users } = item as dynamicObj;
          this.subOrgMap.set(id, name);
          this.subOrgList.push({
            value: id,
            label: name,
            users: users,
          });
        }
      } catch (err) {
        console.warn(
          '[DropdownStore] Failed to fetch sub-organizations:',
          err,
        );
      } finally {
        this.sub_org_list_loading = false;
      }
    },

    getSubOrgName(id: string | null | undefined): string {
      return id ? this.subOrgMap.get(id) || '' : '';
    },

    async getSupplierNameMap() {
      if (this.supplier_name_map_loading) return;
      if (this.supplierNameMap.size > 0) return;

      this.supplier_name_map_loading = true;
      try {
        const response = await masterDataApiService.getSupplierNames();
        if (!response) return;

        const map = new Map<string, string>();
        for (const item of response) {
          if (item.supplier_number && item.supplier_name) {
            map.set(item.supplier_number, item.supplier_name);
          }
        }
        this.supplierNameMap = map;
      } finally {
        this.supplier_name_map_loading = false;
      }
    },

    prepareBooleanList() {
      this.booleanList = [];
      this.booleanList.push({
        value: true,
        label: 'True',
      });
      this.booleanList.push({
        value: false,
        label: 'False',
      });
    },

    async getDocStatusesList() {
      this.doc_status_list = [];
      const response =
        await docStatusNotificationsApiService.getDocumentAlertStatuses();
      if (!response) {
        return;
      }

      response.forEach((statusKey: any, index: any) => {
        this.doc_status_list.push(
          Object.assign({}, { value: statusKey, label: i18n.t(statusKey) }),
        );
      });
    },

    async prepareDocStatusesList() {
      if (this.doc_status_list.length > 1) {
        return;
      }

      await this.getDocStatusesList();
    },

    prepareTimeList() {
      this.time_list = [
        {
          label: i18n.t('minutes'),
          value: 'minutes',
        },
        {
          label: i18n.t('hours'),
          value: 'hours',
        },
        {
          label: i18n.t('days'),
          value: 'days',
        },
      ];
    },

    getRulesTypeList() {
      this.classification_type_list = [
        {
          label: i18n.t('barcode'),
          value: 'barcode',
        },
        {
          label: i18n.t('email'),
          value: 'email',
        },
      ];
    },

    setPoModuleList() {
      this.po_module_list = [];
      this.po_module_list.push(
        Object.assign({}, { value: '2_way', label: i18n.t('2_way') }),
      );
      this.po_module_list.push(
        Object.assign({}, { value: '3_way', label: i18n.t('3_way') }),
      );
      this.po_module_list.push(
        Object.assign({}, { value: '4_way', label: i18n.t('4_way') }),
      );
    },

    getModelList() {
      const modelList = getWithExpiry('model_list');

      if (modelList) {
        this.model_list = modelList;
      } else {
        this.prepareModelList();
      }
    },

    async prepareModelList() {
      this.model_list = [] as any;
      this.model_list.push(
        Object.assign(
          {},
          {
            value: '',
            label: i18n.t('please_select_model'),
            name: 'please_select_model',
          },
        ),
      );

      const response = await aiModelsAndLabelsApiService.getModels();
      if (response.success === false) {
        throw { message: response.message };
      }

      for (const [index, item] of Object.entries(response)) {
        const row = item as dynamicObj;
        if ('TR-OCR-Service' == row.model_type) {
          this.model_list.push(
            Object.assign(
              {},
              { value: row.id, label: row.model_label, name: row.model_name },
            ),
          );
        }
      }

      setWithExpiry('model_list', this.model_list);
    },

    getLanguageList() {
      this.languages_list = [];
      this.languages_list.push(
        Object.assign(
          {},
          { value: DEFAULT_LANGUAGE, label: i18n.t('english'), flag: 'us' },
        ),
      );
      this.languages_list.push(
        Object.assign({}, { value: 'de', label: i18n.t('german'), flag: 'de' }),
      );
      this.languages_list.push(
        Object.assign(
          {},
          { value: 'es-es', label: i18n.t('spanish'), flag: 'es' },
        ),
      );
      this.languages_list.push(
        Object.assign({}, { value: 'fr', label: i18n.t('french'), flag: 'fr' }),
      );
      this.languages_list.push(
        Object.assign(
          {},
          { value: 'nl', label: i18n.t('netherland'), flag: 'nl' },
        ),
      );
      this.languages_list.push(
        Object.assign(
          {},
          { value: 'it', label: i18n.t('italian'), flag: 'it' },
        ),
      );
      this.languages_list.push(
        Object.assign(
          {},
          { value: 'zh', label: i18n.t('chinese'), flag: 'cn' },
        ),
      );
    },

    getFlagImageUrl(code = ''): string {
      // Ensure it's lowercase, as the CDN uses lowercase country codes
      return `https://flagcdn.com/24x18/${code.toLowerCase()}.png`;
    },

    getSelectedLanguage() {
      let locale = LocalStorage.getItem(CONSTANTS.selected_language) as string;

      if (!locale) {
        locale = DEFAULT_LANGUAGE;
      }

      return locale;
    },

    getDocumentTriggers() {
      const triggerList = getWithExpiry('document_triggers_list');

      if (triggerList) {
        this.prepareTriggerList(triggerList);
      } else {
        this.loadDocumentTriggers();
      }
    },

    async loadDocumentTriggers() {
      const triggerList = await workflowApiService.getDocumentTriggers();
      setWithExpiry('document_triggers_list', triggerList);
      this.prepareTriggerList(triggerList);
    },

    prepareTriggerList(triggerList: any) {
      Object.keys(triggerList).forEach((key: any) => {
        this.trigger_list.push({
          value: key,
          label: i18n.t(key),
        });
      });
    },

    getDocumentStatuses() {
      const statusList = getWithExpiry('document_statuses_list');

      if (statusList) {
        this.prepareStatusList(statusList);
      } else {
        this.loadDocumentStatuses();
      }
    },

    async loadDocumentStatuses() {
      const statusList = await workflowApiService.getDocumentStatuses();
      setWithExpiry('document_statuses_list', statusList);
      this.prepareStatusList(statusList);
    },

    prepareStatusList(statusList: any) {
      Object.keys(statusList).forEach((key: any) => {
        this.document_statuses_list.push({
          value: key,
          label: i18n.t(key),
        });
      });
    },

    preparePriorityList() {
      this.priority_list = [
        {
          label: i18n.t('low'),
          value: 'low',
        },
        {
          label: i18n.t('medium'),
          value: 'medium',
        },
        {
          label: i18n.t('high'),
          value: 'high',
        },
      ];
    },
  },
});
